import { hasStakeInHand } from './hasStakeInHand';

/**
 * How long a table's multi-run vote window runs, in seconds.
 *
 * The real value is per-table and comes from the server as
 * `game.multiRunVoteSecs` (resolved there as: the table's own setting → the
 * global MULTIRUNTIME → this fallback). The constant is only what a payload
 * predating EN-663 lands on — it is NOT a mirror of the server timeout, which
 * is exactly the assumption that made the countdown disagree with the server on
 * any environment configured to anything else. It tracks the server's own
 * DEFAULT_VOTE_WINDOW_SECS, which tracks the `settings.MULTIRUNTIME` seed.
 *
 * It lives here, rather than at a single call site, because several surfaces
 * count the same window down: the player's own seat timer, the vote buttons'
 * expiry gate, and the cross-table indicator on the other tables' fans
 * (EN-10237). Two copies of the fallback would be two chances to reintroduce
 * the bug above.
 */
export const FALLBACK_VOTE_WINDOW_SECS = 8;

export const voteWindowSecs = (game: any): number => {
  const secs = Number(game?.multiRunVoteSecs);
  return Number.isFinite(secs) && secs > 0 ? secs : FALLBACK_VOTE_WINDOW_SECS;
};

/**
 * Is this seat inside an open multi-run vote window?
 *
 * The 'v' in `game.runs` is the server saying the window is open; the money
 * test is the same one the prompt itself gates on, so a seat offered no
 * buttons is not given a clock either. It drives the seat's own action timer
 * (SeatWithPlayer): during the window that ring counts the VOTE down instead
 * of the turn, which is why the panel carries no dial of its own.
 */
export const isVoteWindowOpen = (game: any, seatId: number): boolean =>
  String(game?.runs ?? '').includes('v') && hasStakeInHand(game, seatId);
